import type { HeliaNode } from '@/types/poc/helia';
import type { LibP2pWithServices } from '@/types/poc/libp2p';
import type { MessagePayload } from '@/types/poc/message';

export interface UploadSuccessfulResult {
  cid: string;
  sixId: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationStartedResult {
  cid: string;
  sixId: string;
  totalBytes: number;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationProgressResult {
  cid: string;
  sixId: string;
  stage: 'fetching' | 'registering';
  bytesTransferred: number;
  totalBytes: number;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationFailedResult {
  cid: string;
  sixId: string;
  reason: string;
  sender: string;
  messageTimestamp: number;
}

export interface RefreshFilesResult {
  sixId: string;
  sender: string;
  messageTimestamp: number;
}

export interface RefreshSixResult {
  sixId: string;
  sender: string;
  messageTimestamp: number;
}

export interface RefreshSixesResult {
  sender: string;
  messageTimestamp: number;
}

export interface RefreshFileKeysResult {
  sender: string;
  messageTimestamp: number;
}

export interface RefreshFilesToDeleteListResult {
  sender: string;
  messageTimestamp: number;
}

export interface LogoutResult {
  sender: string;
  messageTimestamp: number;
}

export type MessageHandlerResult =
  | { type: 'uploadSuccessful'; data: UploadSuccessfulResult }
  | { type: 'fileReplicationStarted'; data: FileReplicationStartedResult }
  | { type: 'fileReplicationProgress'; data: FileReplicationProgressResult }
  | { type: 'fileReplicationFailed'; data: FileReplicationFailedResult }
  | { type: 'refreshFiles'; data: RefreshFilesResult }
  | { type: 'refreshSix'; data: RefreshSixResult }
  | { type: 'refreshSixes'; data: RefreshSixesResult }
  | { type: 'refreshFileKeys'; data: RefreshFileKeysResult }
  | { type: 'refreshFilesToDeleteList'; data: RefreshFilesToDeleteListResult }
  | { type: 'logout'; data: LogoutResult };

export interface MessageStats {
  topic: string;
  timestamp: number;
  messageType: string;
  // Authenticated publisher peer id (StrictSign), or null for an unsigned message.
  originPeerId: string | null;
}

export abstract class MessagingService {
  abstract setupSubscriptions(
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null,
    onMessage: (result: MessageHandlerResult, stats: MessageStats) => void,
    topics: string[]
  ): () => void;
  abstract processMessage(
    topic: string,
    message: MessagePayload,
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null
  ): Promise<MessageHandlerResult | null>;
}
